import { CaretRightIcon } from '@phosphor-icons/react';
import type { JSX } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { cn } from '@/lib/utils/cn';
import { findNavItemByPath } from '@/lib/utils/nav-items';

/**
 * Simple two-level breadcrumb based on the current pathname:
 *   "Aryan O&M / {NavItem}"
 *
 * Phase 3 ships the basic version; richer per-page breadcrumbs (e.g.,
 * "Aryan O&M / Machines / 123 / Edit") arrive incrementally in feature phases via
 * route handle objects (`<Route handle={{ breadcrumb: 'Edit' }}>` pattern).
 */
export function Breadcrumbs({ className }: { readonly className?: string }): JSX.Element {
  const location = useLocation();
  const navItem = findNavItemByPath(location.pathname);

  return (
    <nav
      aria-label="Breadcrumb"
      className={cn('flex items-center gap-1 text-sm text-muted-foreground overflow-hidden', className)}
    >
      {/* On mobile (<sm) hide the root "Aryan O&M" segment when there's a child page label,
          so the topbar doesn't overflow. Show only the current page label truncated. */}
      <Link
        to="/dashboard"
        className={cn(
          'hover:text-foreground transition-colors shrink-0',
          navItem && 'hidden sm:inline',
        )}
      >
        Aryan O&amp;M
      </Link>
      {navItem && (
        <>
          <CaretRightIcon size={14} className="text-muted-foreground/60 hidden sm:inline shrink-0" />
          <span aria-current="page" className="text-foreground font-medium truncate">
            {navItem.label}
          </span>
        </>
      )}
    </nav>
  );
}
